import { FixedDimension } from "./utils/fixed-dimension";
import { getItemByField } from "./utils/get-items";

export class GraphicObject {
  // Coordinate System of GraphicObject's: (0, 0) in left-bottom corner X increase to right, Y increase to up
  // parent is GraphicObject, canvas is root GraphicObject
  /*
  settings: {
    location: {
      x: new ScalableDimension(0, 0),
      y: new ScalableDimension(0, 0)
    },
    size: {
      width: new ScalableDimension(0, 0),
      height: new ScalableDimension(0, 0)
    },
    // optionals
    margin: {
      left: new ScalableDimension(0, 0),
      right: new ScalableDimension(0, 0),
      top: new ScalableDimension(0, 0),
      bottom: new ScalableDimension(0, 0)
    },
    padding: {
      left: new ScalableDimension(0, 0),
      right: new ScalableDimension(0, 0),
      top: new ScalableDimension(0, 0),
      bottom: new ScalableDimension(0, 0)
    },
    parent: {
      isCustom: false,
      ref: object,
      location: {
        x: 0,
        y: 0
      },
      size: {
        width: 0,
        height: 0
      },
      padding: {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0
      }
    },
    root: {
      ref: object
    }
    tags: [],
    classes: [],
    styles: [],
    transfer: [{ path: "path.to.settings.field", level: -1 }] // -1 - to all childs, >= 0 - deep
  }
  */
  constructor(settings) {
    this.settings = settings;
    if (this.settings.margin === undefined) {
      this.settings.margin = {};
    }
    if (this.settings.margin.left === undefined) {
      this.settings.margin.left = new FixedDimension(0);
    }
    if (this.settings.margin.right === undefined) {
      this.settings.margin.right = new FixedDimension(0);
    }
    if (this.settings.margin.top === undefined) {
      this.settings.margin.top = new FixedDimension(0);
    }
    if (this.settings.margin.bottom === undefined) {
      this.settings.margin.bottom = new FixedDimension(0);
    }
    if (this.settings.padding === undefined) {
      this.settings.padding = {};
    }
    if (this.settings.padding.left === undefined) {
      this.settings.padding.left = new FixedDimension(0);
    }
    if (this.settings.padding.right === undefined) {
      this.settings.padding.right = new FixedDimension(0);
    }
    if (this.settings.padding.top === undefined) {
      this.settings.padding.top = new FixedDimension(0);
    }
    if (this.settings.padding.bottom === undefined) {
      this.settings.padding.bottom = new FixedDimension(0);
    }
    if (this.settings.parent === undefined) {
      this.settings.parent = {};
    }
    if (this.settings.parent.isCustom === undefined) {
      this.settings.parent.isCustom = false;
    }
    if (this.settings.transfer === undefined) {
      this.settings.transfer = [];
    }
    GraphicObject.transferSettings(this.settings.parent.ref?.settings, this.settings);

    GraphicObject.calculateBorders(this.settings);
    this.settings.isDebugMode = false;
    if (this.settings.debug === undefined) {
      this.settings.debug = {};
    }
  }

  addTransfer(fieldName, level = -1) {
    GraphicObject.addSettingsTransfer(this.settings, fieldName, level);
  }

  removeTransfer(fieldName) {
    for (let i = 0; i < this.settings.transfer.length; ++i) {
      if (this.settings.transfer[i].path == fieldName) {
        this.settings.transfer.splice(i, 1);
        return;
      }
    }
  }

  static addSettingsTransfer(settings, fieldName, level = -1) {
    const field = getItemByField("path", fieldName, settings.transfer);
    if (field === undefined) {
      settings.transfer.push({ path: fieldName, level: level });
    }
  }

  static transferSettings(parentSettings, settings) {
    if (parentSettings === undefined || parentSettings.transfer === undefined) {
      return;
    }
    for (let fieldIndex = 0; fieldIndex < parentSettings.transfer.length; ++fieldIndex) {
      const settingsPath = parentSettings.transfer[fieldIndex].path;
      const level = parentSettings.transfer[fieldIndex].level;
      const settingsPathParts = settingsPath.split('.');
      let settingsField = undefined;
      for (let partIndex = 0; partIndex < settingsPathParts.length; ++partIndex) {
        const settingsPathPart = settingsPathParts[partIndex];
        if (settingsField === undefined) {
          settingsField = parentSettings;
        }
        settingsField = settingsField[settingsPathPart];
        if (settingsField === undefined) {
          break;
        }
      }
      if (settingsField !== undefined) {
        let settingsFieldNewLocation = settings;
        for (let partIndex = 0; partIndex < (settingsPathParts.length - 1); ++partIndex) {
          const settingsPathPart = settingsPathParts[partIndex];
          if (settingsFieldNewLocation[settingsPathPart] === undefined) {
            settingsFieldNewLocation[settingsPathPart] = {};
          }
          settingsFieldNewLocation = settingsFieldNewLocation[settingsPathPart];
        }
        settingsFieldNewLocation[settingsPathParts[settingsPathParts.length - 1]] = settingsField;
        if (level < 0 || level > 1) {
          GraphicObject.addSettingsTransfer(settings, settingsPath, level < 0 ? -1 : level-1);
        }
      }
    }
  }

  addTag(tagName) {
    if (!this.settings.tags.includes(tagName)) {
      this.settings.tags.push(tagName);
      this.onAddTag?.(tagName);
    }
  }

  removeTag(tagName) {
    for (let i = 0; i < this.settings.tags.length; ++i) {
      if (this.settings.tags[i] == tagName) {
        this.settings.tags.splice(i, 1);
        this.onRemoveTag?.(tagName);
        return;
      }
    }
  }

  setDebugMode(debugSettings) {
    this.settings.isDebugMode = true;
    this.settings.debug = debugSettings;
  }

  // calculate locations in pixels
  static calculateBorders(settings) {
    // if parent is null, we has html parent object with coord system: (0, 0) in left-top corner
    // in this case object location will have html object's system: (0, 0) in left-top corner
    let parentWidth = undefined;
    let parentHeight = undefined;
    const isCustomParentInfo = settings.parent.isCustom || settings.parent.ref === undefined || settings.parent.ref == null;
    if (isCustomParentInfo) {
      if (settings.parent.size === undefined) {
        settings.parent.size = {};
      }
      if (settings.parent.size.width === undefined) {
        settings.parent.size.width = 0;
      }
      if (settings.parent.size.height === undefined) {
        settings.parent.size.height = 0;
      }
      parentWidth = settings.parent.size.width;
      parentHeight = settings.parent.size.height;
    } else {
      parentWidth = settings.parent.ref.settings.clientSize.width;
      parentHeight = settings.parent.ref.settings.clientSize.height;
    }
    settings.marginInPixels = {
      left: settings.margin.left?.calcValue(parentWidth) ?? 0,
      right: settings.margin.right?.calcValue(parentWidth) ?? 0,
      top: settings.margin.top?.calcValue(parentHeight) ?? 0,
      bottom: settings.margin.bottom?.calcValue(parentHeight) ?? 0
    };
    settings.locationInPixels = {
      x: settings.location.x.calcValue(parentWidth),
      y: settings.location.y.calcValue(parentHeight)
    };
    settings.relatedLocation = {
      x: settings.locationInPixels.x + settings.marginInPixels.left,
      y: settings.locationInPixels.y + settings.marginInPixels.bottom
    };
    if (isCustomParentInfo) {
      if (settings.parent.padding === undefined) {
        settings.parent.padding = {};
      }
      if (settings.parent.padding.left === undefined) {
        settings.parent.padding.left = 0;
      }
      if (settings.parent.padding.right === undefined) {
        settings.parent.padding.right = 0;
      }
      if (settings.parent.padding.bottom === undefined) {
        settings.parent.padding.bottom = 0;
      }
      if (settings.parent.padding.top === undefined) {
        settings.parent.padding.top = 0;
      }
      settings.relatedLocation.x += settings.parent.padding.left;
      settings.relatedLocation.y += settings.parent.padding.bottom;
    } else {
      settings.relatedLocation.x += settings.parent.ref.settings.clientLocation.x;
      settings.relatedLocation.y += settings.parent.ref.settings.clientLocation.y;
    }
    settings.absoluteLocation = {
      x: settings.relatedLocation.x,
      y: settings.relatedLocation.y
    };
    if (isCustomParentInfo) {
      if (settings.parent.location === undefined) {
        settings.parent.location = {};
      }
      if (settings.parent.location.x === undefined) {
        settings.parent.location.x = 0;
      }
      if (settings.parent.location.y === undefined) {
        settings.parent.location.y = 0;
      }
      settings.absoluteLocation.x += settings.parent.location.x;
      settings.absoluteLocation.y += settings.parent.location.y;
    } else {
      settings.absoluteLocation.x += settings.parent.ref.settings.absoluteLocation.x;
      settings.absoluteLocation.y += settings.parent.ref.settings.absoluteLocation.y;
    }
    settings.sizeInPixels = {
      width: settings.size.width.calcValue(parentWidth) - settings.marginInPixels.left - settings.marginInPixels.right,
      height: settings.size.height.calcValue(parentHeight) - settings.marginInPixels.top - settings.marginInPixels.bottom
    };
    settings.paddingInPixels = {
      left: settings.padding.left?.calcValue(settings.sizeInPixels.width) ?? 0,
      right: settings.padding.right?.calcValue(settings.sizeInPixels.width) ?? 0,
      top: settings.padding.top?.calcValue(settings.sizeInPixels.height) ?? 0,
      bottom: settings.padding.bottom?.calcValue(settings.sizeInPixels.height) ?? 0
    };
    settings.clientLocation = {
      x: settings.paddingInPixels.left,
      y: settings.paddingInPixels.bottom
    };
    settings.clientSize = {
      width: settings.sizeInPixels.width - settings.clientLocation.x - settings.paddingInPixels.right,
      height: settings.sizeInPixels.height - settings.clientLocation.y - settings.paddingInPixels.top
    };
  }

  // x: new ScalableDimension(0, 0), y: new ScalableDimension(0, 0)
  // return: true - location is updated, false - location is not updated
  updateLocation(x, y) {
    if (this.settings.location.x.isEquals(x) && this.settings.location.y.isEquals(y)) {
      return false;
    }
    this.setLocation(x, y);
    this.createBorders();
    return true;
  }

  setLocation(x, y) {
    this.settings.location = {
      x: x,
      y: y
    };
  }

  // width: new ScalableDimension(0, 0), height: new ScalableDimension(0, 0)
  // return: true - size is updated, false - size is not updated
  updateSize(width, height) {
    if (this.settings.size.width.isEquals(width) && this.settings.size.height.isEquals(height)) {
      return false;
    }
    this.setSize(width, height);
    this.createBorders();
    return true;
  }

  setSize(width, height) {
    this.settings.size = {
      width: width,
      height: height
    };
  }

  // location: { x: new ScalableDimension(0, 0), y: new ScalableDimension(0, 0) }
  // size: { width: new ScalableDimension(0, 0), height: new ScalableDimension(0, 0) }
  // return: true - borders are updated, false - borders are not updated
  updateBorders(location, size) {
    if (
      this.settings.location.x.isEquals(location.x) &&
      this.settings.location.y.isEquals(location.y) &&
      this.settings.size.width.isEquals(size.width) &&
      this.settings.size.height.isEquals(size.height)
    ) {
      return false;
    }
    this.setBorders(location, size);
    this.createBorders();
    return true;
  }

  setBorders(location, size) {
    this.settings.location = location;
    this.settings.size = size;
  }

  // return: true - borders are updated, false - borders are not updated
  updateParentSize(width, height) {
    if (this.settings.parent.isCustom || this.settings.parent.ref === undefined || this.settings.parent.ref == null) {
      if (this.settings.parent.size.width == width && this.settings.parent.size.height == height) {
        return false;
      }
      this.setParentSize(width, height);
    }
    this.createBorders();
    return true;
  }

  setParentSize(width, height) {
    this.settings.parent.size = {
      width: width,
      height: height
    };
  }

  createBorders() {
    GraphicObject.calculateBorders(this.settings);
  }
}
